import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '~/components/core';
import { Order, Result } from '~/api-services/ReportsService/types';
import { useAIStrategyEnabled, useAppSelector, useStyles } from '~/hooks';
import { appSettingsSelector } from '~/state/selectors';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';
import { FONT_SIZE_XXS } from '~/constants';
import { EditPositionDetail } from '../EditOpenPosition';
import PositionDetail from '../EditOpenPosition/PositionDetail';
import StrategyLinkRow from '../AIStrategies/StrategyLinkRow';
import { formatDate } from '~/lib/format';

type Props = {
    result: Result;
    inStrategyView?: boolean;
};

export default function ResultDetails({ result, inStrategyView }: Props) {
    const styles = useStyles(stylesGenerator);
    const { colorScheme } = useAppSelector(appSettingsSelector);
    const aiStrategyEnabled = useAIStrategyEnabled();
    // Only strategy-opened rows get the combined layout; every other row keeps the plain
    // "Open | timestamp" line it has always had.
    const showStrategyLink = !!result.strategyId && aiStrategyEnabled && !inStrategyView;

    const resultDetails: EditPositionDetail[] = [
        // Left Side
        {
            label: 'position-open',
            value: formatDate(result.openTime),
            valueStyle: styles.textSpacing,
        },
        // Right Side
        {
            label: 'investor-name',
            value: result.investorName,
            valueStyle: styles.textSpacing,
        },
    ];

    return (
        <View
            key={result.id}
            style={[
                styles.orderDetailsContainer,
                colorScheme === 'dark' ? styles.borderColorDark : styles.borderColorWhite,
            ]}
        >
            {showStrategyLink ? (
                // One line instead of the label/link/timestamp stack: link left, open time right.
                // The timestamp needs no "Open" label here — it is the only date in the panel.
                <View style={styles.strategyRow}>
                    <StrategyLinkRow strategyId={result.strategyId} rowStyle={styles.strategyLinkInline} />
                    <Text noTranslate style={styles.strategyRowValue}>
                        {formatDate(result.openTime)}
                    </Text>
                </View>
            ) : (
                <View style={styles.positionDetailsContainer}>
                    <View style={styles.positionDetailsColumn}>
                        {resultDetails.slice(0, 1).map((positionDetail: EditPositionDetail, index) => (
                            <PositionDetail
                                editPositionDetail={positionDetail}
                                rowStyle={[styles.positionDetailsRow, styles.positionDetailsRowLeft]}
                                key={index}
                            />
                        ))}
                    </View>
                </View>
            )}
            {/* A strategy position is never copy-traded, so the investor row is always blank
                there — rendering it would only add empty height under the link. */}
            {showStrategyLink ? null : (
                <View style={styles.positionDetailsContainer}>
                    <View style={styles.positionDetailsColumn}>
                        {resultDetails.slice(1, 2).map((positionDetail: EditPositionDetail, index) => (
                            <PositionDetail
                                editPositionDetail={positionDetail}
                                rowStyle={[styles.positionDetailsRow, styles.positionDetailsRowLeft]}
                                key={index}
                            />
                        ))}
                    </View>
                </View>
            )}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        numberSpacing: { letterSpacing: 0.5 },
        textSpacing: { letterSpacing: 0 },
        positionDetailsColumn: { width: '100%', maxWidth: '100%' },
        positionDetailsContainer: {
            flexDirection: 'row',
            marginVertical: scaleSize(6),
            alignContent: 'center'
        },
        positionDetailsRow: {
            width: '100%',
            paddingVertical: scaleSize(4),
            justifyContent: 'space-between',
        },
        positionDetailsRowLeft: {
            paddingLeft: scaleSize(10),
            paddingRight: scaleSize(0),
        },
        positionDetailsRowRight: {
            paddingRight: scaleSize(10),
            paddingLeft: scaleSize(5),
        },
        strategyRow: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            paddingHorizontal: scaleSize(10),
            paddingVertical: scaleSize(6),
        },
        strategyLinkInline: { width: 'auto', paddingLeft: 0, paddingVertical: 0 },
        strategyRowValue: {
            fontSize: FONT_SIZE_XXS,
            color: theme.text_pendingOrder,
            letterSpacing: 0,
        },
        borderColorDark: { borderColor: theme.background },
        borderColorWhite: { borderColor: theme.border_2 },
        orderDetailsContainer: {
            flexDirection: 'column',
            width: '100%',
            backgroundColor: theme.backgroundSecondary,
            borderBottomWidth: 1,
            borderRightWidth: 1,
            borderLeftWidth: 1,
            borderBottomLeftRadius: scaleSize(5),
            borderBottomRightRadius: scaleSize(5),
        },
    });
